import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { HubSettingsSection } from "@/components/settings/HubSettingsSection";
import { ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";

export type PersonTrainingRow = {
  skillId: number;
  skillName: string;
  trainedOn: string | null;
  expiresOn: string | null;
  trainerStaffId: number | null;
  trainerName: string | null;
  status: "missing" | "current" | "expired";
};

type Skill = { id: number; name: string; active: boolean };
type Choice = { staffId: number; fullName: string };

export function PersonTrainingSection(props: {
  staffId: number;
  training: PersonTrainingRow[];
  managerChoices: Choice[];
  writesAllowed: boolean;
  readOnlyHint: string;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [skillId, setSkillId] = useState("");
  const [trainedOn, setTrainedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [trainerStaffId, setTrainerStaffId] = useState("");
  const [notes, setNotes] = useState("");

  const skillsQuery = useQuery({
    queryKey: ["people", "skills"],
    queryFn: () => apiFetch<{ skills: Skill[] }>("/api/people/skills"),
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/people/staff/${props.staffId}/training`, {
        method: "PUT",
        body: {
          skillId: Number(skillId),
          trainedOn,
          expiresOn,
          trainerStaffId: trainerStaffId === "" ? null : Number(trainerStaffId),
          notes,
        },
      }),
    onSuccess: async () => {
      setNotes("");
      await queryClient.invalidateQueries({ queryKey: ["people", "staff", props.staffId] });
      await queryClient.invalidateQueries({ queryKey: ["people", "training-matrix"] });
      showToast({ message: "Training saved.", tone: "success" });
    },
    onError: (error: Error) => showToast({ message: error.message, tone: "error" }),
  });

  const skills = (skillsQuery.data?.skills ?? []).filter((skill) => skill.active);

  return (
    <HubSettingsSection
      title="Training"
      hoverHint="One row per skill. A later save replaces the dates for that skill. The training matrix reads these dates."
    >
      {props.training.length === 0 ? (
        <p className="mb-3 text-sm text-slate-600">No training recorded.</p>
      ) : (
        <table className="mb-4 min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase text-slate-600">
              <th className="px-2 py-2">Skill</th>
              <th className="px-2 py-2">Trained</th>
              <th className="px-2 py-2">Expires</th>
              <th className="px-2 py-2">Trainer</th>
              <th className="px-2 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {props.training.map((row) => (
              <tr key={row.skillId} className="border-b border-slate-100">
                <td className="px-2 py-2">{row.skillName}</td>
                <td className="px-2 py-2">{row.trainedOn ?? ""}</td>
                <td className="px-2 py-2">{row.expiresOn ?? ""}</td>
                <td className="px-2 py-2">{row.trainerName ?? ""}</td>
                <td className="px-2 py-2">{row.status === "expired" ? "Expired" : "Current"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label htmlFor="training-skill">Skill</Label>
          <select
            id="training-skill"
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={skillId}
            disabled={!props.writesAllowed}
            onChange={(event) => setSkillId(event.target.value)}
          >
            <option value="">Choose a skill</option>
            {skills.map((skill) => (
              <option key={skill.id} value={skill.id}>
                {skill.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="training-trainer">Trainer</Label>
          <select
            id="training-trainer"
            className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm"
            value={trainerStaffId}
            disabled={!props.writesAllowed}
            onChange={(event) => setTrainerStaffId(event.target.value)}
          >
            <option value="">No trainer</option>
            {props.managerChoices.map((choice) => (
              <option key={choice.staffId} value={choice.staffId}>
                {choice.fullName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="training-on">Trained</Label>
          <Input
            id="training-on"
            type="date"
            className="mt-1"
            value={trainedOn}
            disabled={!props.writesAllowed}
            onChange={(event) => setTrainedOn(event.target.value)}
          />
        </div>
        <div>
          <Label htmlFor="training-expires">Expires</Label>
          <Input
            id="training-expires"
            type="date"
            className="mt-1"
            value={expiresOn}
            disabled={!props.writesAllowed}
            onChange={(event) => setExpiresOn(event.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="training-notes">Notes</Label>
          <Input
            id="training-notes"
            className="mt-1"
            value={notes}
            disabled={!props.writesAllowed}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </div>
      <div className="mt-3">
        <ReadOnlyHoverLayer active={!props.writesAllowed} hoverHint={props.readOnlyHint}>
          <Button
            type="button"
            disabled={!props.writesAllowed || skillId === "" || trainedOn === "" || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            Save training
          </Button>
        </ReadOnlyHoverLayer>
      </div>
    </HubSettingsSection>
  );
}
